/**
 * Imperative stereo RMS bars for the Mixer panel.
 * Vertical scaleY (L/R); one rAF for all registered channels + master.
 */
import { meterHeightFromRms, type EngineTelemetry } from "../../../../src/lib/audio";
import { createMeterClock, smoothMeterLevel } from "./meterSmooth";

type ChannelBars = {
  left: HTMLElement;
  right: HTMLElement;
  shownL: number;
  shownR: number;
};

let getTelemetry: (() => EngineTelemetry) | null = null;
const tracks = new Map<number, ChannelBars>();
let master: ChannelBars | null = null;
let rafId = 0;
const clock = createMeterClock();

export function setMixerMeterTelemetry(getter: (() => EngineTelemetry) | null): void {
  getTelemetry = getter;
  if (!getter) {
    stopPump();
    return;
  }
  clock.reset();
  startPump();
}

export function registerMixerTrackMeter(
  track: number,
  bars: { left: HTMLElement; right: HTMLElement } | null,
): void {
  if (!bars) {
    tracks.delete(track);
    if (tracks.size === 0 && !master) {
      stopPump();
    }
    return;
  }
  tracks.set(track, {
    left: bars.left,
    right: bars.right,
    shownL: 0,
    shownR: 0,
  });
  if (getTelemetry) {
    startPump();
  }
}

export function registerMixerMasterMeter(
  bars: { left: HTMLElement; right: HTMLElement } | null,
): void {
  if (!bars) {
    master = null;
    if (tracks.size === 0) {
      stopPump();
    }
    return;
  }
  master = {
    left: bars.left,
    right: bars.right,
    shownL: 0,
    shownR: 0,
  };
  if (getTelemetry) {
    startPump();
  }
}

function startPump(): void {
  if (rafId !== 0) {
    return;
  }
  const tick = () => {
    rafId = requestAnimationFrame(tick);
    paint();
  };
  rafId = requestAnimationFrame(tick);
}

function stopPump(): void {
  if (rafId === 0) {
    return;
  }
  cancelAnimationFrame(rafId);
  rafId = 0;
}

function applyBars(ch: ChannelBars): void {
  ch.left.style.transform = `scaleY(${ch.shownL})`;
  ch.right.style.transform = `scaleY(${ch.shownR})`;
}

function paint(): void {
  if (!getTelemetry) {
    return;
  }
  const dt = clock.dt();
  const telemetry = getTelemetry();
  for (const [slot, ch] of tracks) {
    const t = telemetry.tracks[slot];
    ch.shownL = smoothMeterLevel(ch.shownL, meterHeightFromRms(t?.rmsL ?? 0), dt);
    ch.shownR = smoothMeterLevel(ch.shownR, meterHeightFromRms(t?.rmsR ?? 0), dt);
    applyBars(ch);
  }
  if (master) {
    const m = telemetry.master;
    master.shownL = smoothMeterLevel(master.shownL, meterHeightFromRms(m.rmsL), dt);
    master.shownR = smoothMeterLevel(master.shownR, meterHeightFromRms(m.rmsR), dt);
    applyBars(master);
  }
}
